import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Pourquoi mon store banne est-il bloqué en position ouverte ?",
    a: "Un store banne bloqué en position ouverte peut avoir plusieurs causes : un moteur en panne, des bras qui se sont désaxés, une toile mal enroulée qui coince le mécanisme, ou un problème de fin de course. Ne forcez surtout pas : un forçage peut tordre les bras et aggraver la panne.",
  },
  {
    q: "Mon store banne ne répond plus à la télécommande, que faire ?",
    a: "Vérifiez d'abord les piles de la télécommande. Si cela ne résout pas le problème, il peut s'agir d'une désynchronisation entre la télécommande et le récepteur du moteur, d'un récepteur radio défectueux, ou d'une panne du moteur lui-même. Appelez-nous pour un diagnostic précis.",
  },
  {
    q: "Intervenez-vous le week-end pour un store bloqué ?",
    a: "Oui, nous intervenons 7j/7, y compris le week-end. Un store banne bloqué en position ouverte peut être gênant et exposer votre terrasse. Nous faisons le maximum pour intervenir sous 24-48h.",
  },
];

export default function StoreBanneBloque() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Store banne bloqué à Lyon : dépannage',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Store banne bloqué à Lyon ? Déblocage rapide, 7j/7. Diagnostic moteur, bras, télécommande. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Réparation store à Lyon', item: 'https://depanfenetres-lyon.fr/reparation-store-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Store banne bloqué', item: 'https://depanfenetres-lyon.fr/store-banne-bloque-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Store banne bloqué à Lyon ? Dépannage 7j/7 | DFPRO"
        description="Store banne bloqué ouvert ou fermé à Lyon ? Déblocage moteur, bras, télécommande. Intervention rapide 24-48h, 7j/7. Devis gratuit, garantie 10 ans. DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/store-banne-bloque-lyon/"
        ogTitle="Store banne bloqué à Lyon — Déblocage rapide 7j/7 | DFPRO"
        ogDescription="Store banne coincé ? Déblocage à Lyon 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/reparation-store-lyon/" className="hover:text-primary-500 transition-colors">Réparation store à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Store banne bloqué</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/store-bloque-hero-2026.jpg"
              alt="Store banne bloqué à Lyon — dépannage rapide par DFPRO Lyon"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Store banne bloqué à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Votre store banne est coincé, il ne s'ouvre plus ou ne se ferme plus ? Pas de panique. Nos techniciens le débloquent rapidement à Lyon et dans la métropole, 7j/7.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Store banne bloqué : causes et solutions à Lyon
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Un store banne qui se bloque est une situation frustrante, surtout quand il reste coincé en position ouverte et vous prive de votre terrasse, ou pire, en position fermée par une chaude journée d'été à Lyon. La première règle est simple : <strong className="text-foreground-800">ne jamais forcer</strong> un store bloqué, ni manuellement ni en insistant avec la télécommande.
                  </p>
                  <p>
                    Les causes d'un blocage sont multiples. Le problème peut venir du <strong className="text-foreground-800">moteur</strong> : un condensateur défectueux, un moteur grillé, ou un moteur qui a perdu ses fins de course et ne sait plus où s'arrêter. Il peut aussi s'agir d'un <strong className="text-foreground-800">problème mécanique</strong> : bras désaxé, articulation grippée, toile mal enroulée qui coince le tube d'enroulement, ou vis de fixation desserrée.
                  </p>
                  <p>
                    La <strong className="text-foreground-800">télécommande</strong> est également une source fréquente de panne : piles usées, désynchronisation avec le récepteur, ou récepteur radio défectueux. Parfois, c'est le <strong className="text-foreground-800">capteur de vent</strong> (anémomètre) qui déclenche un blocage de sécurité alors qu'il n'y a pas de vent, ou qui ne réarme pas automatiquement après une rafale.
                  </p>
                  <p>
                    Dans tous les cas, <strong className="text-foreground-800">forcer mécaniquement le store aggravera la panne</strong>. Un bras tordu, une toile déchirée ou un moteur endommagé par un forçage entraîneront des réparations plus coûteuses. Mieux vaut nous appeler au 09 80 80 36 84 : nous établissons un diagnostic précis, et dans la majorité des cas, le déblocage est réalisé en une seule visite.
                  </p>
                  <p>
                    Nous intervenons pour le déblocage de store banne dans tout Lyon et la Métropole de Lyon. En cas de store bloqué en position ouverte, nous traitons votre demande en priorité pour protéger votre équipement des intempéries. <strong className="text-foreground-800">Intervention garantie 10 ans</strong>, comme toutes nos prestations.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services store</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Réparation store à Lyon (pilier)', href: '/reparation-store-lyon/' },
                    { label: 'Réparation store banne', href: '/reparation-store-banne-lyon/' },
                    { label: 'Motorisation de store', href: '/motorisation-store-lyon/' },
                    { label: 'Réparation BSO', href: '/reparation-bso-lyon/' },
                    { label: 'Store électrique', href: '/store-electrique-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Store banne bloqué à Lyon ?"
          subtitle="Ne forcez surtout pas ! Appelez le 09 80 80 36 84, on s'occupe du déblocage."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}